import {
  Button, DialogActions, DialogContent, TextField,
 ToggleButton, ToggleButtonGroup
} from '@mui/material';

import {
  FlexContainer, FontIcon, InputCounter, Question, SelectLabel, Text, TextError
} from 'components';
import { LeakEntryView } from 'models';
import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import shortid from 'shortid';
import { DocumentHelper, translate } from 'utils/helpers';
import { ModalHeader } from './ModalHeader';

const LOCATION_LENGTH_MAX = 100;

export const AddLeakModal = ({
                               currentLeak,
                               onClose,
                               onSubmit
                             }: {
  currentLeak: LeakEntryView,
  onClose: (e?: any) => void,
  onSubmit: (leak: LeakEntryView) => void
}) => {
  const [picturesAdded, setPicturesAdded] = useState((currentLeak && currentLeak.documents) || []);
  const [missingLeakStatus, setMissingLeakStatus] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Initialize react-hook-form with default values from currentLeak
  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: currentLeak || new LeakEntryView()
  });

  const onValidateModal = (data) => {
    if (data.leakRepaired === null) {
      setMissingLeakStatus(true);
      return;
    }

    setMissingLeakStatus(false);
    onSubmit({
      ...data,
      index: currentLeak?.index,
      documents: picturesAdded
    });
  };

  return (
    <form onSubmit={handleSubmit(onValidateModal)}>
      <ModalHeader onClose={onClose}>
        <FontIcon fontSize="2.4rem" icon="icon-hazard" marginRight="1rem" />
        {currentLeak ? translate('modalLeak.editLeak') : translate('modalLeak.newLeak')}
      </ModalHeader>

      <DialogContent style={{ paddingTop: '10px' }}>
        <FlexContainer flexDirection="column">
          <Question label={translate('modalLeak.leakLocationQuestion')}>
            <Controller
                  control={control}
                  name="location"
                  render={({ field }) => (
                    <InputCounter maxLength={LOCATION_LENGTH_MAX} value={field.value || ''}>
                      <TextField
                            autoFocus
                            error={!!errors.location}
                            inputProps={{
                              maxLength: LOCATION_LENGTH_MAX
                            }}
                            label={translate('modalLeak.leakLocation')}
                            minRows={3}
                            multiline
                            required
                            {...field}
                        />
                    </InputCounter>
                  )}
                  rules={{ required: true }}
              />
          </Question>

          <Question label={`${translate('modalLeak.leakRepaired')} :`}>
            <Controller
                  control={control}
                  name="leakRepaired"
                  render={({ field }) => (
                    <ToggleButtonGroup
                          aria-label={translate('modalLeak.leakRepaired')}
                          exclusive
                          size="medium"
                          value={field.value}
                          onChange={(event, value) => {
                            if (value === null) return;
                            field.onChange(value);
                          }}
                      >
                      <ToggleButton aria-label={translate('common.done')} data-cy="addLeakDone" value={true}>
                        {translate('common.done')}
                      </ToggleButton>
                      <ToggleButton aria-label={translate('common.todo')} data-cy="addLeakTodo" value={false}>
                        {translate('common.todo')}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
              />
            {missingLeakStatus && (
              <Text color="var(--red)" margin="0.5rem 0">
                {translate('modalLeak.leakRepairedMissing')}
              </Text>
              )}
          </Question>

          <SelectLabel>{translate('modalLeak.leakPicture')}</SelectLabel>
          <input
                placeholder={translate('modalLeak.leakPictureAdd')}
                type="file"
                onChange={event => DocumentHelper.handleFileUpload({ callback: setPicturesAdded, callbackError: setUploadError, event })}
            />
          {uploadError && <TextError>{uploadError}</TextError>}
          {picturesAdded && picturesAdded.length > 0 && (
            <FlexContainer className="modalPicturesContainer">
              {picturesAdded.map(picture => (
                <img alt={picture.name} key={shortid.generate()} src={picture.base64Content} />
                  ))}
            </FlexContainer>
            )}
        </FlexContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{translate('button.cancel')}</Button>
        <Button color="primary" type="submit">{translate('button.save')}</Button>
      </DialogActions>
    </form>
  );
};
